import { dateSort, numberSort } from 'components/LrGrid';
import { formatBoolToYesNo, formatDecimal, formatDollar, formatModString, formatPercent } from 'helpers/formatters';
import { SubmissionDetails } from 'types/submission.type';
import { ICellRendererParams } from 'ag-grid-enterprise';
import React from 'react';
import { LocationOptionsData } from 'types/types.type';
import { sovModCellRenderer } from 'helpers/sov';
import { ColDef, ColGroupDef } from 'ag-grid-enterprise';

export const getSovColumnDefs = (
    submission: SubmissionDetails,
    isAgent: boolean,
    locationOptions: LocationOptionsData
): (ColDef | ColGroupDef)[] => [
    {
        headerName: 'Location Name',
        valueGetter: ({ data }: any) => {
            return data.markedAsDeleted ? `${data.locationName}*` : data.locationName;
        },
        pinned: 'left',
        lockPinned: true,
    },
    {
        headerName: 'Effective Date',
        comparator: dateSort,
        valueGetter: ({ data }: any) =>
            data.originalEffectiveDate || data.effectiveDate || submission.effectiveDate || '',
    },
    {
        headerName: 'Location Address',
        field: 'address1',
    },
    {
        headerName: 'Location Address 2',
        field: 'address2',
    },
    {
        headerName: 'City',
        field: 'city',
    },
    {
        headerName: 'State',
        field: 'state',
    },
    {
        headerName: 'Zip',
        field: 'zip',
    },
    {
        headerName: 'County',
        field: 'county',
    },
    { headerName: 'Latitude', field: 'locationLat' },
    {
        headerName: 'Longitude',
        field: 'locationLon',
    },
    {
        headerName: 'Class',
        valueGetter: ({ data }: any) => locationOptions.LOCATION_CLASSES[data.locationClassType],
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Year of Construction',
        field: 'yearOfConstruction',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Construction Type',
        valueGetter: ({ data }: any) => locationOptions.LOCATION_CONSTRUCTION_TYPES[data.constructionType],
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Wiring Type',
        valueGetter: ({ data }: any) => locationOptions.LOCATION_WIRING_TYPES[data.wiringType],
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: '#Bldings',
        field: 'numberOfBuildings',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: '#Stories',
        field: 'numberOfStories',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: '#Units',
        field: 'numberOfUnits',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Occ %',
        field: 'occupancyPercentage',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatPercent(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: '#Pools',
        field: 'numberOfPools',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'HW Smoke Det All Units',
        field: 'hwSmokeDetectorAllUnits',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatBoolToYesNo(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'A/S',
        field: 'automaticSprinklers',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatBoolToYesNo(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: '% A/S',
        field: 'automaticSprinklersPercentage',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatPercent(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Roof Type',
        valueGetter: ({ data }: any) => {
            let ret = '';
            if (data.roofType) {
                ret = locationOptions.LOCATION_ROOF_TYPES[data.roofType];
            }
            return ret;
        },
        cellRenderer: (data: ICellRendererParams) => {
            const ret = sovModCellRenderer(data);
            return ret;
        },
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Roof Covering',
        valueGetter: ({ data }: any) => {
            let ret = '';
            if (data.roofCoveringType) {
                ret = locationOptions.LOCATION_ROOF_COVERING_TYPES[data.roofCoveringType];
            }
            return ret;
        },
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Yr of Last Full Roof Replacement',
        field: 'lastYearOfRoofReplacement',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: '% Section 8',
        field: 'percentageSection8',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatPercent(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: '% Owner Occupied',
        field: 'percentageOwnerOccupied',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatPercent(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Owned or Managed',
        valueGetter: ({ data }: any) => {
            let ret = '';
            if (data.ownershipType) {
                ret = locationOptions.LOCATION_OWNERSHIP_TYPES[data.ownershipType];
            }
            return ret;
        },
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: '#Years Owned or Managed',
        field: 'numberOfYearsOwnedManaged',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Flood Zone',
        valueGetter: ({ data }: any) => {
            let ret = '';
            if (data.floodZoneType) {
                ret = locationOptions.LOCATION_FLOOD_ZONE_TYPES[data.floodZoneType];
            }
            return ret;
        },
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Total Square Footage',
        field: 'totalSquareFootage',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatDecimal(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Building Value',
        field: 'buildingValue',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatDollar(val, 0)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'BI/Rental Income Value',
        field: 'biRentalIncomeValue',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatDollar(val, 0)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Personal Property Value',
        field: 'personalPropertyValue',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatDollar(val, 0)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Other Value',
        field: 'otherValue',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatDollar(val, 0)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'TIV',
        field: 'totalInsurableValue',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatDollar(val, 0)),
        aggFunc: 'aggValues',
        pinned: 'right',
        lockPinned: true,
    },
    {
        headerName: 'ITV',
        field: 'insuranceToValue',
        comparator: numberSort,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatDollar(val, 0)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'TIV/Building',
        field: 'tivPerBuilding',
        comparator: numberSort,
        hide: isAgent,
        lockVisible: isAgent,
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatDollar(val, 0)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Mortgagee',
        field: 'mortgagee',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Loss Payee(s)',
        field: 'lossPayee',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Additional Name Insured(s)',
        field: 'additionalNamedInsured',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Additional Interest(s)',
        field: 'additionalInterests',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Roof Square Footage',
        field: 'roofSquareFootage',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatDecimal(val)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Est Roof Cost',
        field: 'roofCost',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatDollar(val, 0)),
        aggFunc: 'aggValues',
    },
    {
        headerName: 'Removed',
        field: 'markedAsDeleted',
        cellRenderer: (data: ICellRendererParams) => sovModCellRenderer(data, (val: any) => formatBoolToYesNo(val)),
        aggFunc: 'aggValues',
    },
];
